import { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Loader2, X, ClipboardPaste, ImageOff, Plus, Tags, Scale } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { parseImageUrls, joinImageUrls } from '@/services/excelService';
import { useConfigStore } from '@/store/useConfigStore';
import { useDataStore } from '@/store/useDataStore';
import { optimizeTitle, callLLMWithImages } from '@/services/llmService';
import { cn } from '@/lib/utils';
import { ImageUploader } from '@/components/ui/ImageUploader';
import { ImageEditorModal } from '@/components/ImageEditorModal';
import type { ExcelField } from '@/types';

interface CellEditModalProps {
    open: boolean;
    onClose: () => void;
    field: ExcelField | null;
    rowIndex: number;
    currentValue: string;
    originalValue: string;
    rowNumber: number;
    onSave: (value: string) => void;
}

export function CellEditModal({
    open, onClose, field, rowIndex, currentValue, originalValue, rowNumber, onSave,
}: CellEditModalProps) {
    const [editValue, setEditValue] = useState('');
    const [optimizing, setOptimizing] = useState(false);
    const [error, setError] = useState('');
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState('');
    const [weightImages, setWeightImages] = useState<string[]>([]);
    const editorOnSaveRef = useRef<((url: string) => void) | null>(null);
    const { titleConfig, skuConfig, llmInstances } = useConfigStore();
    const { recordImageReplacement } = useDataStore();

    useEffect(() => {
        if (open) {
            setEditValue(currentValue);
            setError('');
            setWeightImages([]);
        }
    }, [open, currentValue]);

    if (!field) return null;

    const isImage = field.isImage;
    const isProductName = field.key === 'productName';
    const isDifferent = editValue !== originalValue;

    const llmInstance = llmInstances.find(i => i.id === titleConfig.llmInstanceId) ?? llmInstances[0];
    const llmModel = titleConfig.model || llmInstance?.models[0] || '';
    const canOptimize = isProductName && !!llmInstance && !!llmModel;

    const isSkuSpec = field.key === 'skuSpec1' || field.key === 'skuSpec2';
    const isSkuWeight = field.key === 'skuWeight';

    const skuSpecInstance = llmInstances.find(i => i.id === skuConfig.specLlmInstanceId) ?? llmInstances[0];
    const skuSpecModel = skuConfig.specModel || skuSpecInstance?.models[0] || '';
    const canOptimizeSkuSpec = isSkuSpec && !!skuSpecInstance && !!skuSpecModel && skuConfig.specFields.includes(field.key);

    const skuWeightInstance = llmInstances.find(i => i.id === skuConfig.weightLlmInstanceId) ?? llmInstances[0];
    const skuWeightModel = skuConfig.weightModel || skuWeightInstance?.models[0] || '';
    const canOptimizeSkuWeight = isSkuWeight && !!skuWeightInstance && !!skuWeightModel;

    const handleSave = () => {
        onSave(editValue);
        onClose();
    };

    const handleImageClick = (url: string) => {
        setEditorImageUrl(url);
        editorOnSaveRef.current = (newUrl: string) => {
            if (newUrl && newUrl !== url) recordImageReplacement(url, newUrl);
            setEditValue(prev => prev.replace(url, newUrl));
        };
        setEditorOpen(true);
    };

    const handleOptimize = async () => {
        if (!canOptimize) return;
        setOptimizing(true);
        setError('');
        try {
            const optimized = await optimizeTitle(llmInstance, llmModel, titleConfig.prompt, editValue);
            if (optimized) setEditValue(optimized);
        } catch (err) {
            setError(err instanceof Error ? err.message : '优化失败');
        } finally {
            setOptimizing(false);
        }
    };

    const handleOptimizeSkuSpec = async () => {
        if (!canOptimizeSkuSpec) return;
        setOptimizing(true);
        setError('');
        try {
            const out = await optimizeTitle(skuSpecInstance, skuSpecModel, skuConfig.specPrompt, editValue);
            if (out) setEditValue(out);
        } catch (err) {
            setError(err instanceof Error ? err.message : '优化失败');
        } finally {
            setOptimizing(false);
        }
    };

    const handleOptimizeSkuWeight = async () => {
        if (!canOptimizeSkuWeight) return;
        setOptimizing(true);
        setError('');
        try {
            const row = useDataStore.getState().rows[rowIndex] as Record<string, unknown>;
            const context = [
                `产品名称：${String(row?.productName ?? '')}`,
                `SKU规格1：${String(row?.skuSpec1 ?? '')}`,
                `SKU规格2：${String(row?.skuSpec2 ?? '')}`,
                `平台SKU：${String(row?.platformSku ?? '')}`,
                `币种：${String(row?.currency ?? '')}`,
            ].join('\n');
            // 收集图片：开启「参考 SKU 图片」时带上本行 skuImage，并叠加手动上传的图片
            const images: string[] = [];
            if (skuConfig.weightUseImage) {
                images.push(...parseImageUrls(String(row?.skuImage ?? '')).slice(0, 4));
            }
            images.push(...weightImages.slice(0, 4));
            const out = images.length > 0
                ? await callLLMWithImages(skuWeightInstance, skuWeightModel, skuConfig.weightPrompt, context, images)
                : await optimizeTitle(skuWeightInstance, skuWeightModel, skuConfig.weightPrompt, context);
            const w = parseWeightText(out);
            if (w) setEditValue(w);
            else setError(`无法解析重量：${out}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : '估重失败');
        } finally {
            setOptimizing(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={onClose} title={`编辑 · ${field.label} · 第 ${rowNumber} 行`} width="lg">
                <div className="flex flex-col gap-4">
                    {/* 前后对比区 */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-zinc-500">修改前</span>
                                {isDifferent && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">已修改</span>}
                            </div>
                            {isImage ? (
                                <ImagePreview value={originalValue} onImageClick={handleImageClick} />
                            ) : (
                                <div className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-sm text-zinc-700">
                                    {originalValue || <span className="text-zinc-300">—</span>}
                                </div>
                            )}
                        </div>
                        <div className={cn('rounded-lg border p-3', isDifferent ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 bg-white')}>
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-zinc-600">修改后</span>
                                {isDifferent && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-800">待保存</span>}
                            </div>
                            {isImage ? (
                                <ImagePreview value={editValue} onImageClick={handleImageClick} />
                            ) : (
                                <div className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-sm text-zinc-900">
                                    {editValue || <span className="text-zinc-300">—</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 编辑区 */}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <label className="text-xs font-medium text-zinc-600">{isImage ? '图片管理' : '编辑内容'}</label>
                            {isProductName && (
                                <Button variant={canOptimize ? 'primary' : 'outline'} size="sm" onClick={handleOptimize} disabled={!canOptimize || optimizing}
                                    title={!canOptimize ? '请先在配置中心添加 LLM 实例和模型' : 'AI 优化此标题'}>
                                    {optimizing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                    AI 优化标题
                                </Button>
                            )}
                            {isSkuSpec && (
                                <Button variant={canOptimizeSkuSpec ? 'primary' : 'outline'} size="sm" onClick={handleOptimizeSkuSpec} disabled={!canOptimizeSkuSpec || optimizing}
                                    title={!canOptimizeSkuSpec ? '请在「SKU 配置」中启用 SKU 规格优化并勾选该字段' : 'AI 优化此 SKU 规格'}>
                                    {optimizing ? <Loader2 size={14} className="animate-spin" /> : <Tags size={14} />}
                                    AI 优化SKU规格
                                </Button>
                            )}
                            {isSkuWeight && (
                                <Button variant={canOptimizeSkuWeight ? 'primary' : 'outline'} size="sm" onClick={handleOptimizeSkuWeight} disabled={!canOptimizeSkuWeight || optimizing}
                                    title={!canOptimizeSkuWeight ? '请在「SKU 配置」中启用 SKU 估重并选择模型' : 'AI 估算此 SKU 重量'}>
                                    {optimizing ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                                    AI 估算重量
                                </Button>
                            )}
                        </div>
                        {isImage ? (
                            <ImageEditor value={editValue} onChange={setEditValue} onImageClick={handleImageClick} />
                        ) : (
                            <div className="flex flex-col gap-3">
                                {isSkuWeight && skuConfig.weightUseImage && (
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-zinc-600">参考 SKU 图片</label>
                                        <ImageUploader images={weightImages} onChange={setWeightImages} label="上传图片" max={4} />
                                    </div>
                                )}
                                <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder={`输入${field.label}内容`} autoFocus rows={5} />
                            </div>
                        )}
                        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
                        <Button variant="primary" size="sm" onClick={handleSave}>保存</Button>
                    </div>
                </div>
            </Modal>
            <ImageEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                imageUrl={editorImageUrl}
                onSave={(newUrl) => {
                    editorOnSaveRef.current?.(newUrl);
                    setEditorOpen(false);
                }}
            />
        </>
    );
}

// 从 LLM 返回文本中解析重量（Kg，保留 2 位小数）
function parseWeightText(text: string): string {
    const match = text.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return '';
    const num = parseFloat(match[0]);
    if (isNaN(num)) return '';
    return num.toFixed(2);
}

// 图片预览组件（无上限，不显示 URL）
function ImagePreview({ value, onImageClick }: { value: string; onImageClick?: (url: string) => void }) {
    const [errored, setErrored] = useState<Set<string>>(new Set());
    const urls = parseImageUrls(value);
    if (urls.length === 0) return <span className="text-sm text-zinc-300">—</span>;
    return (
        <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
            {urls.map((url, i) => (
                errored.has(url) ? (
                    <div key={`${url}-${i}`} className="flex h-10 w-10 items-center justify-center rounded border border-zinc-200 bg-zinc-50">
                        <ImageOff size={14} className="text-zinc-300" />
                    </div>
                ) : (
                    <img key={`${url}-${i}`} src={url} alt="" className="h-10 w-10 cursor-pointer rounded border border-zinc-200 object-cover"
                        onError={() => setErrored(prev => new Set(prev).add(url))}
                        onClick={() => onImageClick?.(url)} />
                )
            ))}
        </div>
    );
}

// 图片编辑组件：缩略图网格 + 粘贴添加 + 删除，不显示 URL
function ImageEditor({ value, onChange, onImageClick }: { value: string; onChange: (v: string) => void; onImageClick?: (url: string) => void }) {
    const [urls, setUrls] = useState<string[]>(parseImageUrls(value));
    const [errored, setErrored] = useState<Set<string>>(new Set());
    const [dragOver, setDragOver] = useState(false);
    const pasteRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setUrls(parseImageUrls(value));
    }, [value]);

    const updateUrls = useCallback((newUrls: string[]) => {
        setUrls(newUrls);
        onChange(joinImageUrls(newUrls));
    }, [onChange]);

    const removeAt = (index: number) => {
        updateUrls(urls.filter((_, i) => i !== index));
    };

    // 从剪贴板文本提取 URL
    const extractUrls = (text: string): string[] => {
        return text
            .split(/[\s,，\n\r]+/)
            .map(s => s.trim())
            .filter(s => s.startsWith('http://') || s.startsWith('https://'));
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        const newUrls = extractUrls(text);
        if (newUrls.length > 0) {
            // 去重
            const existing = new Set(urls);
            const toAdd = newUrls.filter(u => !existing.has(u));
            if (toAdd.length > 0) updateUrls([...urls, ...toAdd]);
        }
    };

    const handlePasteClick = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const newUrls = extractUrls(text);
            if (newUrls.length > 0) {
                const existing = new Set(urls);
                const toAdd = newUrls.filter(u => !existing.has(u));
                if (toAdd.length > 0) updateUrls([...urls, ...toAdd]);
            }
        } catch {
            // 剪贴板权限失败，聚焦到文本框手动粘贴
            pasteRef.current?.focus();
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const text = e.dataTransfer.getData('text');
        const newUrls = extractUrls(text);
        if (newUrls.length > 0) {
            const existing = new Set(urls);
            const toAdd = newUrls.filter(u => !existing.has(u));
            if (toAdd.length > 0) updateUrls([...urls, ...toAdd]);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            {/* 图片网格（无上限） */}
            {urls.length > 0 ? (
                <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
                    {urls.map((url, i) => (
                        <div key={`${url}-${i}`} className="group relative aspect-square">
                            {errored.has(url) ? (
                                <div className="flex h-full w-full items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50">
                                    <ImageOff size={16} className="text-zinc-300" />
                                </div>
                            ) : (
                                <img src={url} alt="" className="h-full w-full cursor-pointer rounded-lg border border-zinc-200 object-cover transition-transform hover:scale-105"
                                    onError={() => setErrored(prev => new Set(prev).add(url))}
                                    onClick={() => onImageClick?.(url)} />
                            )}
                            {/* 删除按钮 */}
                            <button
                                onClick={() => removeAt(i)}
                                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-600"
                                title="删除此图片"
                            >
                                <X size={12} />
                            </button>
                            {/* 序号 */}
                            <span className="absolute bottom-0.5 left-0.5 rounded bg-black/50 px-1 text-[9px] text-white">{i + 1}</span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="rounded-lg border-2 border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-400">
                    暂无图片，粘贴或拖入图片链接添加
                </div>
            )}

            {/* 统计 */}
            <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span>共 {urls.length} 张图片</span>
            </div>

            {/* 粘贴/拖拽区域 */}
            <div
                onPaste={handlePaste}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={cn(
                    'flex items-center gap-2 rounded-lg border-2 border-dashed p-2 transition-colors',
                    dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-zinc-200 bg-zinc-50/50',
                )}
            >
                <Button variant="outline" size="sm" onClick={handlePasteClick} className="shrink-0">
                    <ClipboardPaste size={14} /> 粘贴图片链接
                </Button>
                <textarea
                    ref={pasteRef}
                    onPaste={handlePaste}
                    placeholder="或在此粘贴图片链接（支持多个，逗号/换行分隔）"
                    className="min-h-[36px] flex-1 resize-none border-0 bg-transparent p-1.5 text-sm text-transparent caret-zinc-400 focus:outline-none"
                    rows={1}
                />
                <span className="shrink-0 text-xs text-zinc-400">
                    <Plus size={12} className="inline" /> 添加到末尾
                </span>
            </div>
        </div>
    );
}
